import React, { useState, useEffect } from 'react';
import { incidentsAPI } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';
import StateBreachResponse from '../components/StateBreachResponse';
import {
  AlertTriangle,
  Plus,
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
  Shield,
  Search,
  X,
  Trash2,
  ChevronDown,
  ChevronUp,
  Activity
} from 'lucide-react';

const INCIDENT_TYPES = [
  { value: 'malware', label: 'Malware', icon: '🦠' },
  { value: 'phishing', label: 'Phishing', icon: '🎣' },
  { value: 'data_breach', label: 'Data Breach', icon: '💾' },
  { value: 'unauthorized_access', label: 'Unauthorized Access', icon: '🔓' },
  { value: 'ddos', label: 'DDoS Attack', icon: '🌊' },
  { value: 'other', label: 'Other', icon: '⚠️' }
];

const STATUS_CONFIG = {
  open: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Open' },
  investigating: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Investigating' },
  contained: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Contained' },
  eradicated: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Eradicated' },
  recovered: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Recovered' },
  closed: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'Closed' }
};

const IncidentTracker = () => {
  const { notify } = useNotification();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [expandedIncident, setExpandedIncident] = useState(null);
  const [filter, setFilter] = useState({ status: 'all', search: '' });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', incidentType: 'other', severity: 'medium',
    reportedBy: '', affectedSystems: '', rootCause: '', containmentActions: '',
    eradicationActions: '', recoveryActions: '', lessonsLearned: ''
  });
  const [newTimelineEntry, setNewTimelineEntry] = useState('');

  useEffect(() => { fetchIncidents(); }, []);

  const fetchIncidents = async () => {
    try {
      const res = await incidentsAPI.getAll();
      setIncidents(res.data || []);
    } catch (err) { notify.error('Failed to load incidents'); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...form, affectedSystems: form.affectedSystems.split(',').map(s => s.trim()).filter(Boolean) };
      if (selectedIncident) {
        await incidentsAPI.update(selectedIncident.id, data);
        notify.success('Incident updated');
      } else {
        await incidentsAPI.create(data);
        notify.success('Incident created');
      }
      setShowModal(false); setSelectedIncident(null);
      setForm({ title: '', description: '', incidentType: 'other', severity: 'medium', reportedBy: '', affectedSystems: '', rootCause: '', containmentActions: '', eradicationActions: '', recoveryActions: '', lessonsLearned: '' });
      fetchIncidents();
    } catch (err) { notify.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (incidentId, newStatus) => {
    try {
      await incidentsAPI.update(incidentId, { status: newStatus });
      notify.success('Status updated');
      fetchIncidents();
    } catch (err) { notify.error('Failed to update'); }
  };

  const handleAddTimeline = async (incidentId) => {
    if (!newTimelineEntry.trim()) return;
    try {
      await incidentsAPI.addTimeline(incidentId, { action: newTimelineEntry });
      setNewTimelineEntry('');
      fetchIncidents();
    } catch (err) { notify.error('Failed to add entry'); }
  };

  const handleDelete = async (incidentId) => {
    if (!window.confirm('Delete this incident?')) return;
    try {
      await incidentsAPI.delete(incidentId);
      notify.success('Incident deleted');
      fetchIncidents();
    } catch (err) { notify.error('Failed to delete'); }
  };

  const filteredIncidents = incidents.filter(i => {
    if (filter.status !== 'all' && i.status !== filter.status) return false;
    if (filter.search && !i.title.toLowerCase().includes(filter.search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: incidents.length,
    open: incidents.filter(i => i.status === 'open').length,
    investigating: incidents.filter(i => i.status === 'investigating').length,
    closed: incidents.filter(i => i.status === 'closed').length
  };

  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><Loader2 className="w-10 h-10 text-violet-500 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-16">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2"><AlertTriangle className="w-7 h-7 text-red-400" />Incident Response</h1>
            <p className="text-slate-400 mt-1">Track and manage security incidents</p>
          </div>
          <button onClick={() => { setSelectedIncident(null); setForm({ title: '', description: '', incidentType: 'other', severity: 'medium', reportedBy: '', affectedSystems: '', rootCause: '', containmentActions: '', eradicationActions: '', recoveryActions: '', lessonsLearned: '' }); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"><Plus className="w-4 h-4" />Report Incident</button>
        </div>

        {/* State Breach Emergency Response */}
        <div className="mb-8">
          <StateBreachResponse />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"><div className="text-2xl font-bold text-white">{stats.total}</div><div className="text-sm text-slate-400">Total</div></div>
          <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30"><div className="text-2xl font-bold text-red-400">{stats.open}</div><div className="text-sm text-red-300">Open</div></div>
          <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30"><div className="text-2xl font-bold text-yellow-400">{stats.investigating}</div><div className="text-sm text-yellow-300">Investigating</div></div>
          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30"><div className="text-2xl font-bold text-green-400">{stats.closed}</div><div className="text-sm text-green-300">Closed</div></div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Search incidents..." value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })} className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white" />
          </div>
          <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
            <option value="all">All Status</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        {/* Incidents List */}
        {filteredIncidents.length === 0 ? (
          <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center">
            <Shield className="w-16 h-16 text-slate-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Incidents</h3>
            <p className="text-slate-400">No security incidents recorded</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredIncidents.map(incident => {
              const statusConfig = STATUS_CONFIG[incident.status] || STATUS_CONFIG.open;
              const typeConfig = INCIDENT_TYPES.find(t => t.value === incident.incidentType) || INCIDENT_TYPES[5];
              const isExpanded = expandedIncident === incident.id;

              return (
                <div key={incident.id} className="bg-slate-800/50 rounded-xl border border-slate-700">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="text-3xl">{typeConfig.icon}</div>
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="text-lg font-semibold text-white">{incident.title}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${statusConfig.bg} ${statusConfig.text}`}>{statusConfig.label}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${incident.severity === 'critical' ? 'bg-red-500/20 text-red-400' : incident.severity === 'high' ? 'bg-orange-500/20 text-orange-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{incident.severity}</span>
                          </div>
                          <p className="text-slate-400 text-sm mt-1">{incident.description?.substring(0, 100)}{incident.description?.length > 100 ? '...' : ''}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span><Clock className="w-3 h-3 inline mr-1" />{new Date(incident.detectedAt).toLocaleString()}</span>
                            {incident.reportedBy && <span>Reported by: {incident.reportedBy}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <select value={incident.status} onChange={(e) => handleStatusChange(incident.id, e.target.value)} className="px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white">
                          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                        <button onClick={() => setExpandedIncident(isExpanded ? null : incident.id)} className="p-2 text-slate-400 hover:text-white">
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        <button onClick={() => handleDelete(incident.id)} className="p-2 text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-6 pt-6 border-t border-slate-700">
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <h4 className="text-sm font-medium text-slate-300 mb-2">Root Cause</h4>
                            <p className="text-slate-400 text-sm">{incident.rootCause || 'Not documented'}</p>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-slate-300 mb-2">Affected Systems</h4>
                            <p className="text-slate-400 text-sm">{incident.affectedSystems?.join(', ') || 'Not specified'}</p>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-slate-300 mb-2">Containment Actions</h4>
                            <p className="text-slate-400 text-sm">{incident.containmentActions || 'Not documented'}</p>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-slate-300 mb-2">Lessons Learned</h4>
                            <p className="text-slate-400 text-sm">{incident.lessonsLearned || 'Not documented'}</p>
                          </div>
                        </div>
                        {/* Timeline */}
                        <div className="mt-6">
                          <h4 className="text-sm font-medium text-slate-300 mb-3">Timeline</h4>
                          <div className="flex gap-2 mb-4">
                            <input type="text" placeholder="Add timeline entry..." value={newTimelineEntry} onChange={(e) => setNewTimelineEntry(e.target.value)} className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm" />
                            <button onClick={() => handleAddTimeline(incident.id)} className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm">Add</button>
                          </div>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {incident.timeline?.map((entry, idx) => (
                              <div key={entry.id || idx} className="flex items-start gap-3 p-2 bg-slate-700/30 rounded-lg">
                                <Activity className="w-4 h-4 text-violet-400 mt-0.5" />
                                <div>
                                  <p className="text-sm text-slate-300">{entry.action}</p>
                                  <p className="text-xs text-slate-500">{new Date(entry.timestamp).toLocaleString()} - {entry.user}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-2xl border border-slate-700 shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-700 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Report Security Incident</h3>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Title *</label>
                  <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Type</label>
                    <select value={form.incidentType} onChange={(e) => setForm({ ...form, incidentType: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                      {INCIDENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Severity</label>
                    <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Description</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white resize-none" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Reported By</label>
                    <input type="text" value={form.reportedBy} onChange={(e) => setForm({ ...form, reportedBy: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Affected Systems (comma-separated)</label>
                    <input type="text" value={form.affectedSystems} onChange={(e) => setForm({ ...form, affectedSystems: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg">Cancel</button>
                  <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg disabled:opacity-50">{saving ? 'Saving...' : 'Report Incident'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default IncidentTracker;
